import { requireEnv } from '@/lib/env'
/**
 * Apollo Client Configuration (v4)
 *
 * ONE refresh path: the auth link calls `nhost.refreshSession(margin)` before each request. That
 * SDK call does a synchronous, `navigator.locks`-guarded expiry check against the decoded token and
 * only performs a network refresh when the access token is within `margin` of expiry; it also dedupes
 * refreshes across tabs and retries once. We deliberately do NOT re-derive expiry ourselves (the old
 * code did — with `exp` in seconds vs the SDK's ms, so the hand-rolled check misfired) and we do NOT
 * re-implement a refresh mutex, error-link replay, or a background timer — the SDK owns all of that.
 *
 * A JWT error that survives the per-request refresh means the token is genuinely invalid → sign out.
 */

import {
  ApolloLink,
  split,
  from,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
// The browser client is the official Next.js integration's ApolloClient so `PreloadQuery` data
// preloaded in RSC hydrates THIS client's cache (see providers/ApolloProvider.tsx; a future RSC
// PreloadQuery path).
// This module is browser-only (imports nhost + sessionStorage), so the import resolves to the
// browser/SSR build, never the react-server one.
import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs'
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { gateMarkerLink } from '@/lib/gate-marker-link'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient as createWsClient, type Client as WsClient } from 'graphql-ws'
import { nhost } from '@/lib/nhost'
import { getImpersonation, clearImpersonation } from '@/lib/impersonation'
import { roleHeaderForToken } from '@/lib/active-role'
import { createCacheConfig } from '@/lib/apollo-cache'
import { captureGraphQLErrors } from '@/lib/monitoring/graphql-error-capture'
import {
  markUnauthenticatedFailure,
  shouldSkipUnauthenticatedCapture,
} from '@/lib/monitoring/unauthenticated-capture-guard'
import { isBusinessValidationOnly } from '@/lib/business-validation-errors'
import { serializeWsError } from '@/lib/monitoring/ws-error-serializer'

// ============================================================================
// ENVIRONMENT CONFIGURATION
// ============================================================================

const GRAPHQL_HTTP_URL =
  requireEnv(process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL, 'NEXT_PUBLIC_NHOST_GRAPHQL_URL')

const GRAPHQL_WS_URL = GRAPHQL_HTTP_URL.replace('https://', 'wss://').replace('http://', 'ws://')

// Refresh only when the access token is within this many seconds of expiry (mirrors the SDK's own
// per-request middleware). Cheap no-op in the common case.
const REFRESH_MARGIN_SEC = 60

// ============================================================================
// AUTH LINK - attach a fresh JWT (refresh delegated to the SDK)
// ============================================================================

const authLink = new SetContextLink(async (prevContext) => {
  const headers = (prevContext.headers as Record<string, string>) ?? {}

  // Support impersonation: while an "act as user" overlay is active, every request goes out under
  // the minted token. It is self-expiring and NOT refreshable, so use it as-is and never touch
  // nhost.refreshSession (which would refresh the ADMIN's session, not the acted-as one).
  const impersonation = getImpersonation()
  if (impersonation) {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${impersonation.accessToken}`,
        'Content-Type': 'application/json',
      },
    }
  }

  let session
  try {
    session = await nhost.refreshSession(REFRESH_MARGIN_SEC)
  } catch {
    // Transient refresh failure — fall back to the stored token; the request may 401 and trigger
    // re-auth rather than silently dropping the Authorization header.
    session = nhost.getUserSession()
  }
  const token = session?.accessToken
  // Per-tab role override (user-menu role switcher) — official Hasura role
  // selection: x-hasura-role must be within the token's allowed-roles;
  // roleHeaderForToken enforces that and returns null otherwise.
  const roleHeader = roleHeaderForToken(token)
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(roleHeader ? { 'x-hasura-role': roleHeader } : {}),
      'Content-Type': 'application/json',
    },
  }
})

// ============================================================================
// ERROR LINK - a hard auth failure signs out; everything else passes through
// ============================================================================

// One-time guard so a batch of failing requests triggers a single redirect, not a loop.
let redirectingToLogin = false

/**
 * A hard auth failure that survived the per-request refresh in authLink (the token is invalid, not
 * merely expired). Sign out and bounce to /login, preserving the return path. RLS access-denied is
 * NOT treated as an auth failure — it's a legitimate permission denial handled by the caller.
 */
function handleHardAuthFailure() {
  if (typeof window === 'undefined' || redirectingToLogin) return

  // While impersonating, an invalid-jwt/401 is on the (non-refreshable) OVERLAY token — the acted-as
  // session has ended. Drop ONLY the overlay and return to the admin area; the admin's real cookie
  // session is untouched. Never sign the admin out here.
  if (getImpersonation()) {
    clearImpersonation()
    if (!window.location.pathname.startsWith('/admin')) window.location.href = '/admin/users'
    return
  }

  if (window.location.pathname.startsWith('/login')) return
  redirectingToLogin = true
  void nhost.auth.signOut({}).catch(() => {})
  const from = window.location.pathname + window.location.search
  window.location.href = `/login?from=${encodeURIComponent(from)}&reason=session-expired`
}

const errorLink = new ErrorLink(({ error, operation }) => {
  const definition = getMainDefinition(operation.query)
  const operationType =
    definition.kind === 'OperationDefinition' ? definition.operation : 'unknown'
  const variableKeys = Object.keys(operation.variables ?? {})

  if (CombinedGraphQLErrors.is(error)) {
    const isAuthFailure = error.errors.some((e) => e.extensions?.code === 'invalid-jwt')

    // Requests that went out WITHOUT a JWT run as role `public`, which has no
    // permissions — Hasura then reports every field as nonexistent
    // (validation-failed "field not found"), a missing session variable
    // (not-found), or a generic masked error (unknown, when BatchHttpLink
    // coalesces the failing op with siblings). These all fire in the
    // auth-hydration / sign-out races and self-heal on the next authenticated
    // render; NONE can be a real backend bug, because a genuine bug is also hit
    // by the authenticated retry and captured there. So skip capture for any
    // GraphQL failure on an unauthenticated request, regardless of code —
    // enumerating codes let the `unknown`/err-89 variants leak (~1,200
    // events/week were this shape, all role=public in Hasura logs). invalid-jwt
    // is excluded from the skip below so a genuinely-expired token still routes
    // to the sign-out redirect.
    //
    // ⛔ Returning here is NOT enough on its own. Service-layer calls
    // (`gqlRequest`) reach Hasura through this very link and then capture the
    // same failure from their own catch block — so this guard used to log its
    // decision into the breadcrumbs of the issue it thought it had prevented
    // (GlitchTip #2402). `markUnauthenticatedFailure` is what actually carries
    // the decision up to that layer. Do not drop it.
    const ctxHeaders = (operation.getContext().headers ?? {}) as Record<string, string>
    const ranUnauthenticated = !ctxHeaders['Authorization']
    if (shouldSkipUnauthenticatedCapture({ ranUnauthenticated, isAuthFailure })) {
      markUnauthenticatedFailure(operation.operationName)
      console.warn(
        `[Apollo] Skipping capture of unauthenticated ${operation.operationName ?? 'operation'} failure (role public):`,
        error.errors[0]?.message
      )
      return
    }

    // Business-rule rejections (deposit gate, posted-night date/room guards,
    // status-automation transitions) are user-actionable validations the UI
    // already turns into a friendly toast (lib/notifications.ts), not crashes.
    // Don't page on them — GlitchTip #1397/#1398 and the 2026-08-04 triage.
    if (isBusinessValidationOnly(error.errors)) return

    captureGraphQLErrors({
      operationName: operation.operationName,
      operationType,
      errors: error.errors,
      source: 'apollo',
      handled: isAuthFailure, // redirect below — recorded, not hidden
      variableKeys,
    })
    if (isAuthFailure) {
      handleHardAuthFailure()
    }
    return
  }
  if (ServerError.is(error)) {
    // The server responded (5xx/401/...) — always worth an issue. Pure network
    // drops (offline clients) are excluded: they are client connectivity, not bugs.
    captureGraphQLErrors({
      operationName: operation.operationName,
      operationType,
      errors: [{ message: error.message, extensions: { code: `http-${error.statusCode}` } }],
      source: 'apollo',
      handled: error.statusCode === 401,
      variableKeys,
    })
    if (error.statusCode === 401) {
      handleHardAuthFailure()
    }
  }
})

// ============================================================================
// RETRY LINK - transient network / 5xx only (never 4xx)
// ============================================================================

const retryLink = new RetryLink({
  delay: { initial: 300, max: 3000, jitter: true },
  attempts: {
    max: 3,
    retryIf: (error) => {
      const status = (error as { statusCode?: number } | undefined)?.statusCode
      if (typeof status === 'number' && status >= 400 && status < 500) return false
      return Boolean(error)
    },
  },
})

// ============================================================================
// HTTP LINK - For queries and mutations
// ============================================================================

// BatchHttpLink coalesces all operations fired within `batchInterval` ms into ONE HTTP POST.
// The calendar (and every dashboard page) fires many independent queries in the same tick; over a
// ~139ms Saudi→Cloudflare round-trip, 20+ separate POSTs stack up. Batching collapses each wave into
// a single request — the biggest single latency win. Hasura executes the batch and returns an array.
// Mutations batch too (safe: Hasura runs each as its own transaction); subscriptions never reach here
// (split() routes them to WS).
const httpLink = new BatchHttpLink({
  uri: GRAPHQL_HTTP_URL,
  credentials: 'include',
  batchMax: 25, // cap per batch — a full page wave is ~12-15 ops
  batchInterval: 20, // ms window to collect operations before flushing
})

// ============================================================================
// WEBSOCKET LINK - For subscriptions
// ============================================================================

let wsClient: WsClient | null = null

// The most recent underlying socket (from graphql-ws's `opened` event) so error
// captures can report its readyState. graphql-ws doesn't expose it otherwise.
let activeWsSocket: WebSocket | null = null

function createWsLink(): GraphQLWsLink | null {
  if (typeof window === 'undefined') {
    // Return null for SSR - subscriptions only work client-side
    return null
  }

  wsClient = createWsClient({
    url: GRAPHQL_WS_URL,
    connectionParams: async () => {
      // Every new WS connection (including a reconnect after a tenant/property switch, which
      // terminates the socket via reconnectWebSocket()) carries a fresh token. A tenant/property
      // switch calls nhost.refreshSession(0) up front, so by the time we reconnect the SDK's stored
      // token already has the new claims; here we just delegate the ordinary near-expiry refresh to
      // the SDK (same margin as HTTP). During impersonation, use the minted overlay token instead.
      const impersonation = getImpersonation()
      if (impersonation) {
        return { headers: { Authorization: `Bearer ${impersonation.accessToken}` } }
      }
      let session
      try {
        session = await nhost.refreshSession(REFRESH_MARGIN_SEC)
      } catch (err) {
        console.warn('[Apollo WS] Failed to refresh token for connection:', err)
        session = nhost.getUserSession()
      }
      const wsRoleHeader = roleHeaderForToken(session?.accessToken)
      return {
        headers: {
          ...(session?.accessToken && {
            Authorization: `Bearer ${session.accessToken}`,
          }),
          ...(wsRoleHeader ? { 'x-hasura-role': wsRoleHeader } : {}),
        },
      }
    },
    lazy: true,
    retryAttempts: 5,
    shouldRetry: (errOrCloseEvent) => {
      // Extract error message for analysis
      const errorMessage = errOrCloseEvent instanceof Error
        ? errOrCloseEvent.message
        : errOrCloseEvent !== null && typeof errOrCloseEvent === 'object' && 'reason' in errOrCloseEvent
          ? String(errOrCloseEvent.reason)
          : ''

      // Permanent errors that shouldn't retry:
      // - Validation errors (bad query syntax)
      // - Access denied (user doesn't have permission)
      const permanentErrorPatterns = [
        'validation',
        'syntax error',
        'field not found',
        'access-denied',
        'not found in type',
        'Unknown field',
      ]

      const isPermanentError = permanentErrorPatterns.some(pattern =>
        errorMessage.toLowerCase().includes(pattern.toLowerCase())
      )

      if (isPermanentError) {
        console.error('[Apollo WS] Permanent error, not retrying:', errorMessage)
        return false
      }

      // JWT errors are transient - will be fixed by token refresh
      if (errorMessage.includes('JWTExpired') || errorMessage.includes('invalid-jwt')) {
        console.warn('[Apollo WS] JWT error, will refresh token on retry')
        // Token will be refreshed in connectionParams on next attempt
      }

      return true // Retry transient errors (network issues, JWT that can be refreshed)
    },
    retryWait: async (retryCount) => {
      // connectionParams refreshes the token on every (re)connect, so we just back off here.
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** retryCount, 30000)))
    },
    on: {
      opened: (socket) => {
        activeWsSocket = socket instanceof WebSocket ? socket : null
      },
      connected: () => { /* WebSocket connected */ },
      closed: (event) => {
        // WebSocket closed
        // Check if closed due to auth error
        if (event && typeof event === 'object' && 'reason' in event) {
          const reason = String(event.reason || '')
          if (reason.includes('JWTExpired') || reason.includes('invalid-jwt')) {
            // Closed due to JWT error, will reconnect
            // Token will be refreshed on reconnect via connectionParams
          }
        }
      },
      error: (error) => {
        // graphql-ws hands us an Error, a CloseEvent, an (Error)Event, or an array of
        // GraphQL errors. Serialize properly — `String(event)` is "[object Event]" — and
        // carry a per-failure-mode code so GlitchTip groups by close-code, not "unknown".
        const serialized = serializeWsError(error, activeWsSocket?.readyState)
        console.error('[Apollo WS] Error:', serialized.message, error)
        // A bare `error` Event carries no information by design (browsers hide WS
        // failure reasons) and fires on every transient drop — sleep/wake, Wi-Fi
        // switch, proxy idle timeout — after which graphql-ws silently reconnects.
        // Reporting it produced 1,700+ undiagnosable GlitchTip events in a week
        // (issue #873) while every diagnosable failure also arrives with a real
        // code. Report only the codes that say something.
        if (serialized.code !== 'ws-event') {
          captureGraphQLErrors({
            operationName: 'websocket-connection',
            operationType: 'subscription',
            errors: [{ message: serialized.message, extensions: { code: serialized.code } }],
            source: 'apollo-ws',
          })
        }
        // Dispatch event for UI to show subscription error
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('apollo:subscription-error', {
            detail: { error, message: 'Real-time connection lost. Some updates may be delayed.' }
          }))
        }
      },
    },
  })

  return new GraphQLWsLink(wsClient)
}

// ============================================================================
// SPLIT LINK - Route subscriptions to WS, others to HTTP
// ============================================================================

function createSplitLink(): ApolloLink {
  const wsLink = createWsLink()

  // Link order: authLink attaches a fresh token first; errorLink sits above retryLink so it observes
  // the FINAL error after retries are exhausted; httpLink is the transport.
  // gateMarkerLink (D4 review 9): the group gate's ` [group_gate:...]` tag never reaches a screen.
  const httpChain = from([authLink, gateMarkerLink, errorLink, retryLink, httpLink])

  if (!wsLink) {
    // SSR: only use HTTP
    return httpChain
  }

  return split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    httpChain
  )
}

// ============================================================================
// CLIENT CREATION
// ============================================================================

let apolloClient: ApolloClient | null = null

/**
 * The browser Apollo client factory. Doubles as `makeClient` for `ApolloNextAppProvider` (see
 * providers/ApolloProvider.tsx) so the provider, the module singleton, and every imperative consumer
 * (`getApolloClient()`, `apolloClient`, `reconnectWebSocket`, `clearApolloCache`, `resetApolloCache`)
 * all share ONE instance per tab. On the server the integration calls this per request → fresh client
 * (no `ssrMode` flag: the integration manages SSR/streaming itself).
 */
export function getApolloClient(): ApolloClient {
  // For SSR, always create a new client (per-request; the integration owns SSR behavior).
  if (typeof window === 'undefined') {
    return new ApolloClient({
      link: createSplitLink(),
      cache: new InMemoryCache(createCacheConfig()),
    })
  }

  // For CSR, reuse the same client
  if (!apolloClient) {
    apolloClient = new ApolloClient({
      link: createSplitLink(),
      cache: new InMemoryCache(createCacheConfig()),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
        },
        query: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all', // Return partial data with errors for queries
        },
        mutate: {
          // Use 'none' to ensure mutations throw on errors - this makes debugging easier
          // With 'all', errors are returned in response but don't throw, hiding issues
          errorPolicy: 'none',
        },
      },
      devtools: { enabled: process.env.NODE_ENV === 'development' },
    })
  }

  return apolloClient
}

// ============================================================================
// WEBSOCKET RECONNECTION (call on auth changes)
// ============================================================================

export function reconnectWebSocket(): void {
  if (typeof window === 'undefined') return

  if (wsClient) {
    // Terminate existing connection - will auto-reconnect with new token
    wsClient.terminate()
  }
}

// ============================================================================
// CACHE UTILITIES
// ============================================================================

export function clearApolloCache(): void {
  if (apolloClient) {
    // clearStore() throws/rejects with invariant #89 ("Store reset while query was in
    // flight") when any query is mid-flight. For our callers (login/role-change/logout
    // cache hygiene) that is informational — the in-flight result just won't land in the
    // fresh cache — so swallow it rather than surfacing an error toast to the user.
    try {
      apolloClient.clearStore().catch((error) => {
        console.warn('[Apollo] clearStore during in-flight queries (expected on auth changes):', error)
      })
    } catch (error) {
      console.warn('[Apollo] clearStore during in-flight queries (expected on auth changes):', error)
    }
  }
}

export function resetApolloCache(): Promise<void> {
  if (apolloClient) {
    return apolloClient.resetStore().then(() => undefined)
  }
  return Promise.resolve()
}

// Export the client getter for direct access
export { apolloClient }
